import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const groupings = {
  byWeek: (deliveries) => {
    const getWeekRange = (date) => {
      const start = new Date(date);
      const dayOfWeek = start.getDay() === 0 ? 7 : start.getDay();
      const monday = start.getDate() - dayOfWeek + 1;
      start.setDate(monday);
      const end = new Date(start);
      const sunday = end.getDate() + 6;
      end.setDate(sunday);
      return { start, end };
    };

    return deliveries.reduce((groups, delivery) => {
      const date = new Date(delivery.timeslot__date);
      const { start, end } = getWeekRange(date);
      const dateRange = `${start.toISOString()} - ${end.toISOString()}`;

      if (!groups[dateRange]) {
        groups[dateRange] = [];
      }
      groups[dateRange].push(delivery);
      return groups;
    }, {});
  },
};

export function useDeliveries({
  subscription,
  displayed,
  endpoint,
  page = 1,
  category = "",
  groupBy = "byWeek",
  currentDeliveryId = null,
}) {
  const [deliveries, setDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState(null);

  const intl = useIntl();

  const formatters = {
    formatDate: (dateString) => {
      let dateFormatLocale = intl.locale;
      if (dateFormatLocale === "en") {
        dateFormatLocale = "en-CA";
      }

      const date = new Date(dateString);
      const formatter = new Intl.DateTimeFormat(dateFormatLocale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      return formatter.format(date);
    },

    formatWeekHeader: (startDate, endDate, weekNumber) => {
      const weekTranslation = intl.formatMessage(
        { id: "weekNumber" },
        { number: weekNumber.toString().padStart(2, "0") },
      );

      return (
        <span className="week-range">
          <span className="week-number">{weekTranslation}</span>
          <span className="date-range">
            {formatters.formatDate(startDate)} -{" "}
            {formatters.formatDate(endDate)}
          </span>
        </span>
      );
    },

    formatWeekday: (specific_timeslot_weekday, generic_timeslot_weekday, showMovedFrom = true) => {
      if (specific_timeslot_weekday === generic_timeslot_weekday || !showMovedFrom) {
        return intl.formatMessage({ id: `weekday_${specific_timeslot_weekday}` });
      }

      const genericTimeslotDay = intl.formatMessage({
        id: `weekday_${generic_timeslot_weekday}`,
      });
      const specificTimeslotDay = intl.formatMessage({
        id: `weekday_${specific_timeslot_weekday}`,
      });

      return (
        <>
          {specificTimeslotDay}
          <span className="moved-from-text">
            {intl.formatMessage(
              { id: "weekdayMovedFrom" },
              { originalDay: genericTimeslotDay }
            )}
          </span>
        </>
      );
    },


    formatPrice: (amount) => {
      return new Intl.NumberFormat(intl.locale, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
  };

  const fetchDeliveries = async () => {
    if (!subscription?.customer?.id) return;

    try {
      setIsLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        ...(category && { category }),
        ...(currentDeliveryId && { current_delivery_id: currentDeliveryId }),
      });

      const url = `/api/customers/${subscription.customer.id}/${endpoint}/?${params}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error("Failed to fetch deliveries");
      const data = await response.json();

      setDeliveries((prev) =>
        page === 1 ? data.results : [...prev, ...data.results],
      );

      setPagination({
        count: data.count,
        next: data.next,
        previous: data.previous,
      });
    } catch (error) {
      console.error(`Error fetching deliveries:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvoiceClick = async (delivery, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (delivery.invoice_url) {
      window.open(delivery.invoice_url, "_blank");
    } else if (delivery.invoice_gcloud_path) {
      try {
        const response = await fetch(
          `/api/generate-gcloud-signed-url/?path=${encodeURIComponent(
            delivery.invoice_gcloud_path,
          )}`,
        );
        if (!response.ok) throw new Error("Failed to generate PDF URL");

        const data = await response.json();
        window.open(data.url, "_blank");
      } catch (error) {
        console.error("Error generating PDF URL:", error);
      }
    }
  };

  useEffect(() => {
    if (displayed && subscription?.customer?.id) {
      fetchDeliveries();
    }

    return () => {
      if (!displayed) {
        setDeliveries([]);
        setPagination(null);
      }
    };
  }, [displayed, subscription?.customer?.id, page, category, currentDeliveryId]);

  const isPreGrouped = deliveries.length > 0 && "deliveries" in deliveries[0];

  return {
    deliveries,
    isLoading,
    pagination,
    fetchDeliveries,
    handleInvoiceClick,
    formatters,
    groupedDeliveries: isPreGrouped
      ? deliveries
      : groupings[groupBy](deliveries),
  };
}
